import { AlertTriangle, CheckCircle2, Clock, Activity, Package } from 'lucide-react';
import { useHealthMetrics, useOperatorTasks } from '@/hooks/useFinancialData';
import { formatBRL, formatPercent } from '@/lib/format';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { EmptyState } from '@/components/ui/EmptyState';

export function HealthMetrics() {
  const { data, loading } = useHealthMetrics();

  if (loading) {
    return (
      <div className="card p-5 h-full">
        <SectionHeader title="Indicadores de saúde" subtitle="Carregando…" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="card p-5 h-full">
        <SectionHeader title="Indicadores de saúde" subtitle="Sem dados" />
        <EmptyState
          icon={Activity}
          title="Sem dados suficientes"
          description="Os indicadores de saúde financeira serão calculados após a importação do extrato."
        />
      </div>
    );
  }

  const items = [
    {
      label: 'Conciliação',
      value: formatPercent(data.reconciliation_pct),
      hint: `${data.reconciled} de ${data.total} transações`,
    },
    {
      label: 'Runway',
      value: data.runway_meses > 0 ? `${data.runway_meses.toFixed(1)} m` : '—',
      hint: 'no ritmo atual de despesas',
    },
    {
      label: 'Saldo total',
      value: formatBRL(data.balance_cents),
      hint: 'todas as contas',
    },
    {
      label: 'Sem categoria',
      value: data.uncategorized.toString(),
      hint: 'transações a classificar',
    },
    {
      label: 'Inadimplência',
      value: formatBRL(data.overdue_receivables_cents),
      hint: 'a receber em atraso',
    },
    {
      label: 'Desp. média/mês',
      value: formatBRL(data.avg_monthly_expense_cents),
      hint: 'últimos 3 meses',
    },
  ];

  return (
    <div className="card p-5 h-full">
      <SectionHeader title="Indicadores de saúde" subtitle="Termômetro do período" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-xl border border-border p-3">
            <p className="stat-label">{it.label}</p>
            <p className="font-display text-lg font-bold text-ink mt-1 tabular-nums">{it.value}</p>
            <p className="text-[11px] text-ink-soft">{it.hint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TopVendors() {
  return (
    <div className="card p-5 h-full">
      <SectionHeader title="Top fornecedores (mês)" subtitle="Por valor de despesa" />
      <EmptyState
        icon={Package}
        title="Disponível após classificação"
        description="Classifique as transações por fornecedor para ver o ranking."
      />
    </div>
  );
}

export function OperatorTasks() {
  const { tasks, loading } = useOperatorTasks();

  const iconFor = (kind: string) => {
    if (kind === 'classify') return Clock;
    if (kind === 'reconcile') return AlertTriangle;
    if (kind === 'approve') return AlertTriangle;
    return CheckCircle2;
  };

  return (
    <div className="card p-5 h-full">
      <SectionHeader
        title="Tarefas pendentes do BPO"
        subtitle="Trabalho do operador para hoje"
      />
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-14 rounded-xl bg-surface-subtle animate-pulse" />)}
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={CheckCircle2}
          title="Tudo em dia"
          description="Nenhuma tarefa pendente no momento."
        />
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const Icon = iconFor(t.kind);
            return (
              <li
                key={t.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-border hover:bg-surface-subtle cursor-pointer transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-warning/10 text-warning flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink">{t.label}</p>
                </div>
                <span className="chip-warning">{t.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
